import { CheckCircle2, Info, X, XCircle, AlertTriangle, type LucideIcon } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { Button, cx } from "./controls";

function useEscape(onClose: () => void, active: boolean) {
  useEffect(() => {
    if (!active) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, active]);
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: "md" | "lg" | "xl";
}) {
  useEscape(onClose, open);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) ref.current?.querySelector<HTMLElement>("input, select, textarea, button:not([data-close])")?.focus();
  }, [open]);
  if (!open) return null;
  return createPortal(
    <div className="ds-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} className={cx("ds-modal", size && size !== "md" && `ds-modal--${size}`)} role="dialog" aria-modal="true">
        <div className="ds-modal__header">
          <div>
            <h2 className="ds-modal__title">{title}</h2>
            {description ? <p className="ds-muted" style={{ margin: "4px 0 0" }}>{description}</p> : null}
          </div>
          <Button variant="ghost" size="sm" icon={X} onClick={onClose} aria-label="Close" data-close />
        </div>
        <div className="ds-modal__body">{children}</div>
        {footer ? <div className="ds-modal__footer">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

export function Drawer({ open, onClose, title, children, footer, width }: { open: boolean; onClose: () => void; title: ReactNode; children?: ReactNode; footer?: ReactNode; width?: number }) {
  useEscape(onClose, open);
  if (!open) return null;
  return createPortal(
    <>
      <div className="ds-drawer-overlay" onClick={onClose} />
      <aside className="ds-drawer" role="dialog" aria-modal="true" style={width ? { width: `min(${width}px, 100vw)` } : undefined}>
        <div className="ds-modal__header" style={{ borderBottom: "1px solid var(--divider)", paddingBottom: 16 }}>
          <h2 className="ds-modal__title">{title}</h2>
          <Button variant="ghost" size="sm" icon={X} onClick={onClose} aria-label="Close" />
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 24 }}>{children}</div>
        {footer ? <div className="ds-modal__footer">{footer}</div> : null}
      </aside>
    </>,
    document.body,
  );
}

/* ---- Confirm dialog (promise-based) ---- */
interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  tone?: "danger" | "primary";
}
type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;
const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(ConfirmOptions & { resolve: (v: boolean) => void }) | null>(null);
  const confirm = useCallback<ConfirmFn>((opts) => new Promise((resolve) => setState({ ...opts, resolve })), []);
  const close = (v: boolean) => {
    state?.resolve(v);
    setState(null);
  };
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={!!state}
        onClose={() => close(false)}
        title={state?.title ?? ""}
        footer={
          <>
            <Button onClick={() => close(false)}>Cancel</Button>
            <Button variant={state?.tone === "danger" ? "danger" : "primary"} onClick={() => close(true)}>
              {state?.confirmLabel ?? "Confirm"}
            </Button>
          </>
        }
      >
        {state?.message ? <div className="ds-muted">{state.message}</div> : null}
      </Modal>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside ConfirmProvider");
  return ctx;
}

/* ---- Toasts ---- */
type ToastTone = "info" | "success" | "danger" | "warning";
interface ToastItem {
  id: number;
  tone: ToastTone;
  title: string;
  message?: string;
  /** Optional one-click action, e.g. Undo. */
  action?: { label: string; onClick: () => void };
}
interface ToastApi {
  show: (t: Omit<ToastItem, "id">) => void;
  success: (title: string, message?: string) => void;
  error: (title: string, message?: string) => void;
  info: (title: string, message?: string) => void;
}
const ToastContext = createContext<ToastApi | null>(null);
const TOAST_ICON = { info: Info, success: CheckCircle2, danger: XCircle, warning: AlertTriangle };

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const show = useCallback((t: Omit<ToastItem, "id">) => {
    const id = ++seq.current;
    setItems((prev) => [...prev.slice(-3), { ...t, id }]);
    window.setTimeout(() => setItems((prev) => prev.filter((x) => x.id !== id)), t.tone === "danger" ? 8000 : 4500);
  }, []);
  const api: ToastApi = {
    show,
    success: (title, message) => show({ tone: "success", title, message }),
    error: (title, message) => show({ tone: "danger", title, message }),
    info: (title, message) => show({ tone: "info", title, message }),
  };
  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="ds-toasts" aria-live="polite">
          {items.map((t) => {
            const Icon = TOAST_ICON[t.tone];
            return (
              <div key={t.id} className={cx("ds-toast", t.tone !== "info" && `ds-toast--${t.tone}`)}>
                <Icon size={18} />
                <div className="ds-grow">
                  <div className="ds-toast__title">{t.title}</div>
                  {t.message ? <div className="ds-toast__msg">{t.message}</div> : null}
                </div>
                {t.action ? <Button variant="ghost" size="sm" onClick={() => { t.action?.onClick(); setItems((p) => p.filter((x) => x.id !== t.id)); }}>{t.action.label}</Button> : null}
                <Button variant="ghost" size="sm" icon={X} aria-label="Dismiss" onClick={() => setItems((p) => p.filter((x) => x.id !== t.id))} />
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx;
}

/* ---- Dropdown menu ---- */
export interface MenuItem {
  label: string;
  icon?: LucideIcon;
  onSelect: () => void;
  danger?: boolean;
  divider?: boolean;
  disabled?: boolean;
}

export function Menu({ trigger, items, align = "right" }: { trigger: (props: { onClick: () => void; "aria-expanded": boolean }) => ReactNode; items: MenuItem[]; align?: "left" | "right" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const k = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("mousedown", h);
    window.addEventListener("keydown", k);
    return () => {
      window.removeEventListener("mousedown", h);
      window.removeEventListener("keydown", k);
    };
  }, [open]);
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex" }} onClick={(e) => e.stopPropagation()}>
      {trigger({ onClick: () => setOpen((v) => !v), "aria-expanded": open })}
      {open ? (
        <div className="ds-menu" role="menu" style={{ top: "calc(100% + 4px)", [align]: 0 }}>
          {items.map((it, i) =>
            it.divider ? (
              <hr key={i} />
            ) : (
              <button
                key={i}
                role="menuitem"
                className={it.danger ? "ds-menu__item is-danger" : "ds-menu__item"}
                disabled={it.disabled}
                onClick={() => {
                  setOpen(false);
                  it.onSelect();
                }}
              >
                {it.icon ? <it.icon size={15} /> : null}
                {it.label}
              </button>
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}
